import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Globe, Loader2 } from 'lucide-react';
import { addPeriod, normalizeDate, startOfPeriod } from '../../../../utils/dateUtils';
import { getAvailabilityForMonth } from '../../../../services/demoService';
import NavButtons from '../components/NavButtons';
import StepHeading from '../components/StepHeading';
import { BD } from '../bookDemoTheme';

export type BookOutcome = 'booked' | 'slot_taken' | 'error';

interface ScheduleStepProps {
  onBook: (startTime: string) => Promise<BookOutcome>;
  onBack: () => void;
}

/** Local `YYYY-MM-DD`, so slots group by the day the visitor actually sees. */
const dayKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const ScheduleStep: React.FC<ScheduleStepProps> = ({ onBook, onBack }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const timezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const today = useMemo(() => normalizeDate(new Date()), []);
  const currentMonth = useMemo(() => startOfPeriod(today, 'month'), [today]);

  const [viewMonth, setViewMonth] = useState<Date>(currentMonth);
  const [slots, setSlots] = useState<Date[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Date | null>(null);
  const [isBooking, setIsBooking] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);

  const loadSlots = useCallback(
    async (month: Date, options?: { force?: boolean }) => {
      setIsLoading(true);
      setLoadFailed(false);

      try {
        // Resolves instantly when the contact step already warmed this month.
        const available = await getAvailabilityForMonth(startOfPeriod(month, 'month'), options);
        setSlots(available.map(slot => new Date(slot.startTime)));
      } catch (error) {
        console.error('Failed to load demo availability', error);
        setSlots([]);
        setLoadFailed(true);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadSlots(viewMonth);
  }, [viewMonth, loadSlots]);

  const slotsByDay = useMemo(() => {
    const grouped = new Map<string, Date[]>();
    slots.forEach(slot => {
      const key = dayKey(slot);
      grouped.set(key, [...(grouped.get(key) ?? []), slot]);
    });
    grouped.forEach(daySlots => daySlots.sort((a, b) => a.getTime() - b.getTime()));
    return grouped;
  }, [slots]);

  const weekdayLabels = useMemo(() => {
    // 2024-01-01 was a Monday — the calendar is Monday-first regardless of locale.
    return Array.from({ length: 7 }, (_, i) =>
      new Date(2024, 0, 1 + i).toLocaleDateString(locale, { weekday: 'short' })
    );
  }, [locale]);

  const monthLabel = viewMonth.toLocaleDateString(locale, { month: 'long', year: 'numeric' });

  const cells = useMemo(() => {
    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();
    const leading = (new Date(year, month, 1).getDay() + 6) % 7; // Monday-first offset
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const result: (Date | null)[] = Array(leading).fill(null);
    for (let day = 1; day <= daysInMonth; day++) result.push(new Date(year, month, day));
    while (result.length % 7 !== 0) result.push(null);
    return result;
  }, [viewMonth]);

  const daySlots = selectedDay ? slotsByDay.get(dayKey(selectedDay)) ?? [] : [];
  const timeFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }),
    [locale]
  );

  const goToMonth = (step: number) => {
    setSelectedDay(null);
    setSelectedSlot(null);
    setBookingError(null);
    setViewMonth(startOfPeriod(addPeriod(viewMonth, 'month', step), 'month'));
  };

  const handleBook = async () => {
    if (!selectedSlot) return;
    setIsBooking(true);
    setBookingError(null);

    const outcome = await onBook(selectedSlot.toISOString());
    setIsBooking(false);

    if (outcome === 'booked') return;
    setSelectedSlot(null);
    setBookingError(t(outcome === 'slot_taken' ? 'bookDemo.schedule.slotTaken' : 'bookDemo.schedule.bookingFailed'));
    // Force past the cache: the whole point of this path is that the cached slots are stale.
    if (outcome === 'slot_taken') loadSlots(viewMonth, { force: true });
  };

  const atCurrentMonth = viewMonth.getTime() <= currentMonth.getTime();

  return (
    <div className="flex flex-col flex-1">
      <StepHeading title={t('bookDemo.schedule.title')} subtitle={t('bookDemo.schedule.subtitle')} />

      <div className="w-full max-w-[900px] mx-auto grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_260px] gap-5">
        <div className={`rounded-[20px] border p-4 sm:p-5 ${BD.cardBg} ${BD.border}`}>
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={() => goToMonth(-1)}
              disabled={atCurrentMonth}
              aria-label={t('bookDemo.schedule.previousMonth')}
              className={`w-8 h-8 rounded-lg flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed ${BD.textMuted}`}
            >
              <ChevronLeft size={20} />
            </button>
            <p className={`text-[18px] sm:text-[20px] leading-6 font-semibold ${BD.textPrimary}`}>{monthLabel}</p>
            <button
              type="button"
              onClick={() => goToMonth(1)}
              aria-label={t('bookDemo.schedule.nextMonth')}
              className={`w-8 h-8 rounded-lg flex items-center justify-center ${BD.textMuted}`}
            >
              <ChevronRight size={20} />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekdayLabels.map(label => (
              <div key={label} className={`text-center text-[13px] leading-5 font-medium py-2 ${BD.textMuted}`}>
                {label}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {cells.map((date, index) => {
              if (!date) return <div key={`empty-${index}`} />;

              const isPast = date.getTime() < today.getTime();
              const hasSlots = slotsByDay.has(dayKey(date));
              const isDisabled = isPast || !hasSlots;
              const isSelected = selectedDay !== null && dayKey(selectedDay) === dayKey(date);

              return (
                <div key={dayKey(date)} className="flex items-center justify-center">
                  <button
                    type="button"
                    disabled={isDisabled}
                    onClick={() => {
                      setSelectedDay(date);
                      setSelectedSlot(null);
                      setBookingError(null);
                    }}
                    className={`h-10 w-10 rounded-full text-[15px] leading-5 font-medium transition-colors disabled:cursor-not-allowed ${
                      isSelected
                        ? `${BD.accentBg} ${BD.textOnAccent}`
                        : isDisabled
                          ? BD.textFaint
                          : `${BD.textPrimary} ${BD.hoverTile}`
                    }`}
                  >
                    {date.getDate()}
                  </button>
                </div>
              );
            })}
          </div>

          {isLoading && (
            <div className={`flex items-center justify-center gap-2 pt-4 text-[14px] ${BD.textMuted}`}>
              <Loader2 size={16} className="animate-spin" />
              {t('bookDemo.schedule.loadingSlots')}
            </div>
          )}
          {!isLoading && loadFailed && (
            <p className={`pt-4 text-center text-[14px] ${BD.errorText}`}>{t('bookDemo.schedule.loadFailed')}</p>
          )}
          {!isLoading && !loadFailed && slotsByDay.size === 0 && (
            <p className={`pt-4 text-center text-[14px] ${BD.textMuted}`}>{t('bookDemo.schedule.noSlotsThisMonth')}</p>
          )}
        </div>

        <div className={`rounded-[20px] border p-4 sm:p-5 ${BD.cardBg} ${BD.border}`}>
          <p className={`text-[16px] leading-6 font-semibold mb-1 ${BD.textPrimary}`}>
            {selectedDay
              ? selectedDay.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' })
              : t('bookDemo.schedule.pickADay')}
          </p>
          <p className={`flex items-center gap-1.5 text-[13px] leading-5 mb-4 ${BD.textMuted}`}>
            <Globe size={14} />
            {timezone}
          </p>

          {selectedDay && daySlots.length === 0 && (
            <p className={`text-[14px] ${BD.textMuted}`}>{t('bookDemo.schedule.noSlotsThisDay')}</p>
          )}

          <div className="grid grid-cols-3 lg:grid-cols-1 gap-2 max-h-[320px] overflow-y-auto">
            {daySlots.map(slot => {
              const isSelected = selectedSlot !== null && selectedSlot.getTime() === slot.getTime();
              return (
                <button
                  key={slot.toISOString()}
                  type="button"
                  onClick={() => {
                    setSelectedSlot(slot);
                    setBookingError(null);
                  }}
                  className={`h-10 rounded-lg border text-[14px] leading-5 font-semibold transition-colors ${
                    isSelected
                      ? `${BD.accentBg} ${BD.textOnAccent} border-transparent`
                      : `${BD.ghostButton} ${BD.borderStrong}`
                  }`}
                >
                  {timeFormatter.format(slot)}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {bookingError && (
        <p className={`mt-5 mx-auto max-w-[900px] w-full rounded-lg border px-4 py-3 text-[14px] ${BD.errorBox}`}>
          {bookingError}
        </p>
      )}

      <NavButtons
        onBack={onBack}
        onContinue={handleBook}
        continueLabel={isBooking ? t('bookDemo.schedule.booking') : t('bookDemo.schedule.confirm')}
        continueDisabled={!selectedSlot || isBooking}
      />
    </div>
  );
};

export default ScheduleStep;
